import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { TextField } from '@toqio/toqio-web-components';

/**
 * Field — design-system TextField wired to react-hook-form.
 *
 * Usage inside a step (within a <FormProvider>):
 *
 *   import { Field } from '../../shared/Field';
 *
 *   // Minimal usage — just needs the field name:
 *   <Field<MyFlowValues> name="firstName" label="First name" />
 *
 *   // With extra hints:
 *   <Field<MyFlowValues> name="email" label="Email" type="email" placeholder="you@example.com" />
 *
 *   // With explicit control (useful outside a FormProvider):
 *   <Field<MyFlowValues> name="email" label="Email" control={control} />
 *
 * The component bridges the gap between react-hook-form's event-based API
 * and the design system's value-based `onChange: (newValue: string) => void`.
 *
 * Validation errors from react-hook-form are surfaced via the TextField's
 * `error` prop, which renders the error message inline below the input.
 */
type FieldProps<
  TValues extends FieldValues,
  TName extends FieldPath<TValues> = FieldPath<TValues>,
> = {
  /** Field name — must match a key in the form schema. */
  name: TName;
  /** Explicit RHF control. Optional when used inside a <FormProvider>. */
  control?: Control<TValues>;
  /** Label displayed above the input. */
  label?: string;
  /** Placeholder text shown when the input is empty. */
  placeholder?: string;
  /** HTML input type (default: "text"). */
  type?: string;
  /** Whether the field is disabled. */
  disabled?: boolean;
};

export function Field<
  TValues extends FieldValues,
  TName extends FieldPath<TValues> = FieldPath<TValues>,
>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  disabled = false,
}: FieldProps<TValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController<TValues, TName>({ name, control });

  return (
    <TextField
      label={label}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      // TextField uses string values; coerce to string in case RHF stores a non-string.
      value={field.value == null ? '' : String(field.value)}
      // Bridge: DS calls onChange(newValue: string); RHF expects an event or value.
      onChange={(newValue: string) => field.onChange(newValue)}
      onBlur={field.onBlur}
      error={error?.message}
      id={`field-${String(name)}`}
      htmlFor={`field-${String(name)}`}
    />
  );
}
